import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  adminService,
  PricingSettings,
  SubscriptionPlanItem,
  CreditPackageItem,
  SubscriptionPlanCreate,
  SubscriptionPlanUpdate,
  CreditPackageCreate,
  CreditPackageUpdate,
} from '../../../services/adminService';
import { invalidatePlansEntitlementsCache } from '../../../utils/entitlements';
import AdminTabNav from '../AdminPage/AdminTabNav';
import './AdminPricingSettingsPage.css';

function generalSnapshot(t: number, st: number, sn: number, sms: number, email: number, c: string) {
  return JSON.stringify({ t, st, sn, sms, email, c });
}

interface AdminPricingSettingsPageProps {
  embedded?: boolean;
}

const blankPlan = (): SubscriptionPlanCreate => ({
  plan_id: '', name: '', price: 0, credits: 0,
  billing_interval: 'monthly', billing_interval_count: 1,
  description: '', features: [], is_popular: false, sort_order: 0,
  inbound_enabled: false,
  sms_enabled: false,
});

const blankPackage = (): CreditPackageCreate => ({
  package_id: '', name: '', price: 0, credits: 0,
  description: '', is_popular: false, sort_order: 0,
});

// ─── Confirm-delete modal (only modal left) ──────────────────────────────────
const ConfirmDeleteModal: React.FC<{
  label: string;
  activeUsers?: number;
  onConfirm: () => void;
  onForceDelete?: () => void;
  onCancel: () => void;
}> = ({ label, activeUsers, onConfirm, onForceDelete, onCancel }) => (
  <div className="admin-pricing-modal-overlay" role="dialog" aria-modal="true">
    <div className="admin-pricing-modal">
      <h3 className="admin-pricing-modal-title">Delete "{label}"?</h3>
      {activeUsers && activeUsers > 0 ? (
        <>
          <p className="admin-pricing-modal-text admin-pricing-modal-warning">
            {activeUsers} user{activeUsers > 1 ? 's are' : ' is'} currently subscribed to this plan.
          </p>
          <p className="admin-pricing-modal-text">
            <strong>Force Delete</strong> will cancel their Stripe subscriptions and downgrade them to free (no billing).
          </p>
          <p className="admin-pricing-modal-text" style={{ fontSize: '0.8rem' }}>
            Tip: Use <strong>Archive</strong> instead to hide the plan from new users while letting existing subscribers continue.
          </p>
          <div className="admin-pricing-modal-actions">
            <button className="admin-pricing-btn-secondary" onClick={onCancel}>Cancel</button>
            {onForceDelete && <button className="admin-pricing-btn-danger" onClick={onForceDelete}>Force Delete & Cancel Users</button>}
          </div>
        </>
      ) : (
        <>
          <p className="admin-pricing-modal-text">This action is permanent and cannot be undone.</p>
          <div className="admin-pricing-modal-actions">
            <button className="admin-pricing-btn-secondary" onClick={onCancel}>Cancel</button>
            <button className="admin-pricing-btn-danger" onClick={onConfirm}>Delete</button>
          </div>
        </>
      )}
    </div>
  </div>
);

// ─── Inline Plan edit form ────────────────────────────────────────────────────
const PlanInlineEdit: React.FC<{
  initial: SubscriptionPlanCreate | SubscriptionPlanItem;
  isNew: boolean;
  saving: boolean;
  error: string;
  onSave: (data: SubscriptionPlanCreate) => void;
  onCancel: () => void;
}> = ({ initial, isNew, saving, error, onSave, onCancel }) => {
  const [form, setForm] = useState<SubscriptionPlanCreate>({
    plan_id: (initial as any).plan_id ?? '',
    name: initial.name ?? '',
    price: initial.price ?? 0,
    credits: (initial as any).credits ?? 0,
    billing_interval: (initial as any).billing_interval ?? 'monthly',
    billing_interval_count: (initial as any).billing_interval_count ?? 1,
    description: initial.description ?? '',
    features: [...((initial as any).features ?? [])],
    is_popular: (initial as any).is_popular ?? false,
    inbound_enabled: (initial as any).inbound_enabled ?? false,
    sms_enabled: (initial as any).sms_enabled ?? false,
    sort_order: (initial as any).sort_order ?? 0,
  });
  const [featuresText, setFeaturesText] = useState(((initial as any).features ?? []).join(', '));
  const set = (k: keyof SubscriptionPlanCreate, v: any) => setForm(p => ({ ...p, [k]: v }));

  return (
    <div className="admin-pricing-inline-edit">
      <h3 className="admin-pricing-inline-edit-title">{isNew ? 'Add New Plan' : `Edit: ${(initial as any).plan_id?.toUpperCase()}`}</h3>
      {error && <div className="admin-pricing-alert admin-pricing-alert-error" style={{ marginBottom: 10 }}>{error}</div>}
      <div className="admin-pricing-inline-edit-grid">
        <div className="admin-pricing-field">
          <label className="admin-pricing-label-sm">Plan ID <span className="admin-pricing-required">*</span></label>
          <input className="admin-pricing-input-sm" value={form.plan_id}
            onChange={e => set('plan_id', e.target.value.toLowerCase().replace(/\s+/g, '_'))}
            disabled={!isNew} placeholder="e.g. starter" />
          {isNew && <p className="admin-pricing-help">Lowercase, no spaces</p>}
        </div>
        <div className="admin-pricing-field">
          <label className="admin-pricing-label-sm">Display Name <span className="admin-pricing-required">*</span></label>
          <input className="admin-pricing-input-sm" value={form.name} onChange={e => set('name', e.target.value)} placeholder="e.g. Starter" />
        </div>
        <div className="admin-pricing-field">
          <label className="admin-pricing-label-sm">Price</label>
          <input type="number" className="admin-pricing-input-sm" value={form.price} min={0} step={0.01}
            onChange={e => set('price', parseFloat(e.target.value) || 0)} />
        </div>
        <div className="admin-pricing-field">
          <label className="admin-pricing-label-sm">Credits per Period</label>
          <input type="number" className="admin-pricing-input-sm" value={form.credits} min={0}
            onChange={e => set('credits', parseInt(e.target.value) || 0)} />
        </div>
        <div className="admin-pricing-field">
          <label className="admin-pricing-label-sm">Billing Interval</label>
          <select className="admin-pricing-input-sm" value={form.billing_interval}
            onChange={e => set('billing_interval', e.target.value)}>
            <option value="monthly">Monthly</option>
            <option value="yearly">Yearly</option>
          </select>
        </div>
        <div className="admin-pricing-field">
          <label className="admin-pricing-label-sm">Interval Count</label>
          <input type="number" className="admin-pricing-input-sm" value={form.billing_interval_count} min={1}
            onChange={e => set('billing_interval_count', parseInt(e.target.value) || 1)} />
          <p className="admin-pricing-help">1 = every 1 month, 3 = quarterly</p>
        </div>
        <div className="admin-pricing-field admin-pricing-field-full">
          <label className="admin-pricing-label-sm">Description</label>
          <input className="admin-pricing-input-sm" value={form.description ?? ''} onChange={e => set('description', e.target.value)} />
        </div>
        <div className="admin-pricing-field admin-pricing-field-full">
          <label className="admin-pricing-label-sm">Features (comma-separated)</label>
          <textarea className="admin-pricing-textarea-sm" rows={2}
            value={featuresText}
            onChange={e => setFeaturesText(e.target.value)} />
        </div>
        <div className="admin-pricing-field">
          <label className="admin-pricing-label-sm">Sort Order</label>
          <input type="number" className="admin-pricing-input-sm" value={form.sort_order ?? 0} min={0}
            onChange={e => set('sort_order', parseInt(e.target.value) || 0)} />
        </div>
        <div className="admin-pricing-field admin-pricing-checkbox-row admin-pricing-checkbox-row-next-to-sort-order">
          <label className="admin-pricing-checkbox-label">
            <input type="checkbox" checked={!!form.is_popular} onChange={e => set('is_popular', e.target.checked)} />
            Mark as Popular
          </label>
          <label className="admin-pricing-checkbox-label">
            <input type="checkbox" checked={!!form.inbound_enabled} onChange={e => set('inbound_enabled', e.target.checked)} />
            Inbound Enabled
          </label>
          <label className="admin-pricing-checkbox-label">
            <input type="checkbox" checked={!!form.sms_enabled} onChange={e => set('sms_enabled', e.target.checked)} />
            SMS Enabled
          </label>
        </div>
      </div>
      <div className="admin-pricing-inline-edit-actions">
        <button className="admin-pricing-btn-secondary" onClick={onCancel} disabled={saving}>Cancel</button>
        <button className="admin-pricing-btn-primary" onClick={() => onSave({ ...form, features: featuresText.split(',').map((f: string) => f.trim()).filter(Boolean) })} disabled={saving || !form.plan_id || !form.name}>
          {saving ? 'Saving…' : isNew ? 'Add Plan' : 'Save Changes'}
        </button>
      </div>
    </div>
  );
};

// ─── Inline Package edit form ─────────────────────────────────────────────────
const PackageInlineEdit: React.FC<{
  initial: CreditPackageCreate | CreditPackageItem;
  isNew: boolean;
  saving: boolean;
  error: string;
  onSave: (data: CreditPackageCreate) => void;
  onCancel: () => void;
}> = ({ initial, isNew, saving, error, onSave, onCancel }) => {
  const [form, setForm] = useState<CreditPackageCreate>({
    package_id: (initial as any).package_id ?? '',
    name: initial.name ?? '',
    price: initial.price ?? 0,
    credits: initial.credits ?? 0,
    description: initial.description ?? '',
    is_popular: (initial as any).is_popular ?? false,
    sort_order: (initial as any).sort_order ?? 0,
  });
  const set = (k: keyof CreditPackageCreate, v: any) => setForm(p => ({ ...p, [k]: v }));

  return (
    <div className="admin-pricing-inline-edit">
      <h3 className="admin-pricing-inline-edit-title">{isNew ? 'Add New Package' : `Edit: ${(initial as any).package_id?.toUpperCase()}`}</h3>
      {error && <div className="admin-pricing-alert admin-pricing-alert-error" style={{ marginBottom: 10 }}>{error}</div>}
      <div className="admin-pricing-inline-edit-grid">
        <div className="admin-pricing-field">
          <label className="admin-pricing-label-sm">Package ID <span className="admin-pricing-required">*</span></label>
          <input className="admin-pricing-input-sm" value={form.package_id}
            onChange={e => set('package_id', e.target.value.toLowerCase().replace(/\s+/g, '_'))}
            disabled={!isNew} placeholder="e.g. small" />
          {isNew && <p className="admin-pricing-help">Lowercase, no spaces</p>}
        </div>
        <div className="admin-pricing-field">
          <label className="admin-pricing-label-sm">Display Name <span className="admin-pricing-required">*</span></label>
          <input className="admin-pricing-input-sm" value={form.name} onChange={e => set('name', e.target.value)} placeholder="e.g. Small Credit Pack" />
        </div>
        <div className="admin-pricing-field">
          <label className="admin-pricing-label-sm">Price</label>
          <input type="number" className="admin-pricing-input-sm" value={form.price} min={0} step={0.01}
            onChange={e => set('price', parseFloat(e.target.value) || 0)} />
        </div>
        <div className="admin-pricing-field">
          <label className="admin-pricing-label-sm">Credits</label>
          <input type="number" className="admin-pricing-input-sm" value={form.credits} min={0}
            onChange={e => set('credits', parseInt(e.target.value) || 0)} />
        </div>
        <div className="admin-pricing-field admin-pricing-field-full">
          <label className="admin-pricing-label-sm">Description</label>
          <input className="admin-pricing-input-sm" value={form.description ?? ''} onChange={e => set('description', e.target.value)} />
        </div>
        <div className="admin-pricing-field">
          <label className="admin-pricing-label-sm">Sort Order</label>
          <input type="number" className="admin-pricing-input-sm" value={form.sort_order ?? 0} min={0}
            onChange={e => set('sort_order', parseInt(e.target.value) || 0)} />
        </div>
        <div className="admin-pricing-field admin-pricing-field-checkbox">
          <label className="admin-pricing-checkbox-label">
            <input type="checkbox" checked={!!form.is_popular} onChange={e => set('is_popular', e.target.checked)} />
            Mark as Popular
          </label>
        </div>
      </div>
      <div className="admin-pricing-inline-edit-actions">
        <button className="admin-pricing-btn-secondary" onClick={onCancel} disabled={saving}>Cancel</button>
        <button className="admin-pricing-btn-primary" onClick={() => onSave(form)} disabled={saving || !form.package_id || !form.name}>
          {saving ? 'Saving…' : isNew ? 'Add Package' : 'Save Changes'}
        </button>
      </div>
    </div>
  );
};

// ─── Main page ────────────────────────────────────────────────────────────────
const AdminPricingSettingsPage: React.FC<AdminPricingSettingsPageProps> = ({ embedded = false }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const [loadingGeneral, setLoadingGeneral] = useState(true);
  const [savingGeneral, setSavingGeneral] = useState(false);
  const [trialCredits, setTrialCredits] = useState(20);
  const [secondsPerCreditTest, setSecondsPerCreditTest] = useState(30);
  const [secondsPerCreditNormal, setSecondsPerCreditNormal] = useState(30);
  const [creditsPerSms, setCreditsPerSms] = useState(0.1);
  const [creditsPerEmail, setCreditsPerEmail] = useState(0.1);
  const [currency, setCurrency] = useState('aud');
  const lastGeneralSnapshot = useRef<string | null>(null);
  // Must not useMemo only on field state: lastGeneralSnapshot is a ref updated on save/load,
  // so memo would stay stale and "Save" would still show after a successful save.
  const hasGeneralChanges =
    lastGeneralSnapshot.current !== null &&
    generalSnapshot(trialCredits, secondsPerCreditTest, secondsPerCreditNormal, creditsPerSms, creditsPerEmail, currency) !==
      lastGeneralSnapshot.current;

  const [plans, setPlans] = useState<SubscriptionPlanItem[]>([]);
  const [loadingPlans, setLoadingPlans] = useState(true);
  const [showArchivedPlans, setShowArchivedPlans] = useState(false);
  const [addingPlan, setAddingPlan] = useState(false);
  const [editingPlanId, setEditingPlanId] = useState<string | null>(null);
  const [deletingPlanId, setDeletingPlanId] = useState<string | null>(null);
  const [deletingPlanActiveUsers, setDeletingPlanActiveUsers] = useState<number>(0);
  const [planSaving, setPlanSaving] = useState(false);
  const [planError, setPlanError] = useState('');

  const [packages, setPackages] = useState<CreditPackageItem[]>([]);
  const [loadingPackages, setLoadingPackages] = useState(true);
  const [showArchivedPackages, setShowArchivedPackages] = useState(false);
  const [addingPackage, setAddingPackage] = useState(false);
  const [editingPackageId, setEditingPackageId] = useState<string | null>(null);
  const [deletingPackageId, setDeletingPackageId] = useState<string | null>(null);
  const [packageSaving, setPackageSaving] = useState(false);
  const [packageError, setPackageError] = useState('');

  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const [pendingPath, setPendingPath] = useState<string | null>(null);

  const currencySymbols: Record<string, string> = {
    usd: '$', eur: '€', gbp: '£', aud: 'A$', cad: 'C$',
    jpy: '¥', cny: '¥', nzd: 'NZ$', sgd: 'S$', inr: '₹',
  };

  useEffect(() => { loadAll(); }, []);
  const loadAll = () => Promise.all([loadGeneral(), loadPlans(), loadPackages()]);

  const loadGeneral = async () => {
    try {
      setLoadingGeneral(true);
      const data: PricingSettings = await adminService.getPricingSettings();
      setTrialCredits(data.trial_credits);
      setSecondsPerCreditTest(data.seconds_per_credit_test);
      setSecondsPerCreditNormal(data.seconds_per_credit_normal);
      setCreditsPerSms(data.credits_per_sms ?? 0.1);
      setCreditsPerEmail(data.credits_per_email ?? 0.1);
      setCurrency(data.currency);
      lastGeneralSnapshot.current = generalSnapshot(
        data.trial_credits,
        data.seconds_per_credit_test,
        data.seconds_per_credit_normal,
        data.credits_per_sms ?? 0.1,
        data.credits_per_email ?? 0.1,
        data.currency,
      );
    } catch (e: any) { setError(e.response?.data?.detail || 'Failed to load general settings'); }
    finally { setLoadingGeneral(false); }
  };
  const loadPlans = async () => {
    try { setLoadingPlans(true); setPlans(await adminService.listSubscriptionPlans()); }
    catch (e: any) { setError(e.response?.data?.detail || 'Failed to load plans'); }
    finally { setLoadingPlans(false); }
  };
  const loadPackages = async () => {
    try { setLoadingPackages(true); setPackages(await adminService.listCreditPackages()); }
    catch (e: any) { setError(e.response?.data?.detail || 'Failed to load packages'); }
    finally { setLoadingPackages(false); }
  };

  const showSuccess = (msg: string) => { setSuccessMessage(msg); setTimeout(() => setSuccessMessage(''), 3000); };
  const notifyEntitlementsUpdated = () => {
    invalidatePlansEntitlementsCache();
    window.dispatchEvent(new CustomEvent('entitlements:updated'));
  };

  const handleSaveGeneral = async () => {
    try {
      setSavingGeneral(true); setError('');
      await adminService.updatePricingSettings({
        trial_credits: trialCredits,
        seconds_per_credit_test: secondsPerCreditTest,
        seconds_per_credit_normal: secondsPerCreditNormal,
        credits_per_sms: creditsPerSms,
        credits_per_email: creditsPerEmail,
        currency,
      });
      lastGeneralSnapshot.current = generalSnapshot(trialCredits, secondsPerCreditTest, secondsPerCreditNormal, creditsPerSms, creditsPerEmail, currency);
      showSuccess('General settings saved!');
    } catch (e: any) { setError(e.response?.data?.detail || 'Failed to save'); }
    finally { setSavingGeneral(false); }
  };

  // ── Plan CRUD ────────────────────────────────────────────────────────────────
  const handleAddPlan = async (data: SubscriptionPlanCreate) => {
    const cleaned = { ...data, features: (data.features || []).filter(Boolean) };
    try { setPlanSaving(true); setPlanError(''); await adminService.addSubscriptionPlan(cleaned); setAddingPlan(false); await loadPlans(); showSuccess('Plan added!'); }
    catch (e: any) { setPlanError(e.response?.data?.detail || 'Failed to add plan'); }
    finally { setPlanSaving(false); }
    notifyEntitlementsUpdated();
  };
  const handleEditPlan = async (data: SubscriptionPlanCreate) => {
    if (!editingPlanId) return;
    try {
      setPlanSaving(true); setPlanError('');
      const update: SubscriptionPlanUpdate = {
        name: data.name,
        price: data.price,
        credits: data.credits,
        billing_interval: data.billing_interval,
        billing_interval_count: data.billing_interval_count,
        description: data.description,
        features: (data.features || []).filter(Boolean),
        is_popular: data.is_popular,
        inbound_enabled: data.inbound_enabled,
        sms_enabled: data.sms_enabled,
        sort_order: data.sort_order,
      };
      await adminService.updateSubscriptionPlan(editingPlanId, update);
      setEditingPlanId(null); await loadPlans(); showSuccess('Plan updated!');
    } catch (e: any) { setPlanError(e.response?.data?.detail || 'Failed to update plan'); }
    finally { setPlanSaving(false); }
    notifyEntitlementsUpdated();
  };
  const handleToggleArchivePlan = async (planId: string) => {
    try { await adminService.toggleArchiveSubscriptionPlan(planId); await loadPlans(); }
    catch (e: any) { setError(e.response?.data?.detail || 'Failed'); }
    notifyEntitlementsUpdated();
  };
  const handleDeletePlan = async (force: boolean = false) => {
    if (!deletingPlanId) return;
    try {
      await adminService.deleteSubscriptionPlan(deletingPlanId, force);
      setDeletingPlanId(null); setDeletingPlanActiveUsers(0);
      await loadPlans(); showSuccess('Plan deleted.');
    } catch (e: any) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 409 && detail?.active_users) {
        setDeletingPlanActiveUsers(detail.active_users);
      } else {
        setError(typeof detail === 'string' ? detail : detail?.message || 'Failed to delete');
        setDeletingPlanId(null); setDeletingPlanActiveUsers(0);
      }
    }
  };

  // ── Package CRUD ─────────────────────────────────────────────────────────────
  const handleAddPackage = async (data: CreditPackageCreate) => {
    try { setPackageSaving(true); setPackageError(''); await adminService.addCreditPackage(data); setAddingPackage(false); await loadPackages(); showSuccess('Package added!'); }
    catch (e: any) { setPackageError(e.response?.data?.detail || 'Failed to add'); }
    finally { setPackageSaving(false); }
  };
  const handleEditPackage = async (data: CreditPackageCreate) => {
    if (!editingPackageId) return;
    try {
      setPackageSaving(true); setPackageError('');
      const update: CreditPackageUpdate = { name: data.name, price: data.price, credits: data.credits, description: data.description, is_popular: data.is_popular, sort_order: data.sort_order };
      await adminService.updateCreditPackage(editingPackageId, update);
      setEditingPackageId(null); await loadPackages(); showSuccess('Package updated!');
    } catch (e: any) { setPackageError(e.response?.data?.detail || 'Failed to update'); }
    finally { setPackageSaving(false); }
  };
  const handleToggleArchivePackage = async (packageId: string) => {
    try { await adminService.toggleArchiveCreditPackage(packageId); await loadPackages(); }
    catch (e: any) { setError(e.response?.data?.detail || 'Failed'); }
  };
  const handleDeletePackage = async () => {
    if (!deletingPackageId) return;
    try { await adminService.deleteCreditPackage(deletingPackageId); setDeletingPackageId(null); await loadPackages(); showSuccess('Package deleted.'); }
    catch (e: any) { setError(e.response?.data?.detail || 'Failed to delete'); setDeletingPackageId(null); }
  };

  // ── Navigation guard ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (!hasGeneralChanges) return;
    const handleClick = (e: MouseEvent) => {
      const link = (e.target as HTMLElement).closest('a[href]');
      if (!link) return;
      const href = (link as HTMLAnchorElement).getAttribute('href');
      if (!href || href.startsWith('http') || href.startsWith('mailto:') || href.startsWith('tel:')) return;
      const path = href.startsWith('/') ? href : new URL(href, window.location.origin).pathname;
      if (path === location.pathname) return;
      e.preventDefault(); e.stopPropagation();
      setPendingPath(path); setShowLeaveModal(true);
    };
    document.addEventListener('click', handleClick, true);
    return () => document.removeEventListener('click', handleClick, true);
  }, [hasGeneralChanges, location.pathname]);
  useEffect(() => {
    const fn = (e: BeforeUnloadEvent) => { if (hasGeneralChanges) e.preventDefault(); };
    window.addEventListener('beforeunload', fn);
    return () => window.removeEventListener('beforeunload', fn);
  }, [hasGeneralChanges]);

  // ── Derived ──────────────────────────────────────────────────────────────────
  const activePlans = plans.filter(p => p.is_active);
  const archivedPlans = plans.filter(p => !p.is_active);
  const activePackages = packages.filter(p => p.is_active);
  const archivedPackages = packages.filter(p => !p.is_active);
  // Fallback chain: mapped symbol → uppercase ISO code → empty string.
  // No hardcoded 'AUD' — on IN deployments the admin currency is INR, and
  // briefly flashing 'AUD' while `currency` loads is the bug LT-200 fixes.
  const currSym = currencySymbols[currency] || currency?.toUpperCase() || '';
  const deletingPlanLabel = plans.find(p => p.plan_id === deletingPlanId)?.name ?? '';
  const deletingPackageLabel = packages.find(p => p.package_id === deletingPackageId)?.name ?? '';

  // ── Helper: render a plan card (view mode) ──────────────────────────────────
  const renderPlanCard = (plan: SubscriptionPlanItem) => {
    const archived = !plan.is_active;
    return (
    <div key={plan.plan_id} className={`admin-pricing-package ${archived ? 'admin-pricing-package-archived' : ''}`}>
      <div className="admin-pricing-package-header">
        <div>
          <h3 className="admin-pricing-package-id">{plan.name.toUpperCase()}</h3>
          <p className="admin-pricing-package-name">{plan.plan_id}</p>
        </div>
        <div className="admin-pricing-package-badges">
          {plan.is_popular && <span className="admin-pricing-badge admin-pricing-badge-popular">Popular</span>}
          <span className={`admin-pricing-badge ${archived ? 'admin-pricing-badge-archived' : 'admin-pricing-badge-active'}`}>
            <span className="admin-pricing-badge-dot" aria-hidden="true" />
            {archived ? 'Archived' : 'Active'}
          </span>
          <span className={`admin-pricing-badge admin-pricing-badge-inbound ${plan.inbound_enabled ? 'admin-pricing-badge-status-on' : 'admin-pricing-badge-status-off'}`}>
            <span className="admin-pricing-badge-dot" aria-hidden="true" />
            Inbound: {plan.inbound_enabled ? 'On' : 'Off'}
          </span>
          <span className={`admin-pricing-badge admin-pricing-badge-sms ${plan.sms_enabled ? 'admin-pricing-badge-status-on' : 'admin-pricing-badge-status-off'}`}>
            <span className="admin-pricing-badge-dot" aria-hidden="true" />
            SMS: {plan.sms_enabled ? 'On' : 'Off'}
          </span>
        </div>
      </div>
      <div className="admin-pricing-package-info">
        <span><strong>{currSym}{plan.price}</strong> / {plan.billing_interval_count > 1 ? `${plan.billing_interval_count} ` : ''}{plan.billing_interval}</span>
        <span><strong>{plan.credits}</strong> credits</span>
      </div>
      {plan.description && <p className="admin-pricing-package-desc">{plan.description}</p>}
      {plan.features && plan.features.length > 0 && (
        <ul className="admin-pricing-features-list">
          {plan.features.map((f, i) => <li key={i}>{f}</li>)}
        </ul>
      )}
      <div className="admin-pricing-package-meta">
        <span>Sort Order: <strong>{plan.sort_order}</strong></span>
        <span>Billing: <strong>{plan.billing_interval_count > 1 ? `Every ${plan.billing_interval_count} ${plan.billing_interval}s` : `${plan.billing_interval}`}</strong></span>
      </div>
      <div className="admin-pricing-package-actions">
        {!archived && (
          <button className="admin-pricing-action-btn" onClick={() => { setPlanError(''); setEditingPlanId(plan.plan_id); }}>Edit</button>
        )}
        {archived
          ? <button className="admin-pricing-action-btn admin-pricing-action-btn-success" onClick={() => handleToggleArchivePlan(plan.plan_id)}>Unarchive</button>
          : <button className="admin-pricing-action-btn admin-pricing-action-btn-warn" onClick={() => handleToggleArchivePlan(plan.plan_id)}>Archive</button>
        }
        <button className="admin-pricing-action-btn admin-pricing-action-btn-danger" onClick={() => setDeletingPlanId(plan.plan_id)}>Delete</button>
      </div>
    </div>
    );
  };

  // ── Helper: render a package card (view mode) ───────────────────────────────
  const renderPackageCard = (pkg: CreditPackageItem) => {
    const archived = !pkg.is_active;
    return (
    <div key={pkg.package_id} className={`admin-pricing-package ${archived ? 'admin-pricing-package-archived' : ''}`}>
      <div className="admin-pricing-package-header">
        <div>
          <h3 className="admin-pricing-package-id">{pkg.name.toUpperCase()}</h3>
          <p className="admin-pricing-package-name">{pkg.package_id}</p>
        </div>
        <div className="admin-pricing-package-badges">
          {pkg.is_popular && <span className="admin-pricing-badge admin-pricing-badge-popular">Popular</span>}
          <span className={`admin-pricing-badge ${archived ? 'admin-pricing-badge-archived' : 'admin-pricing-badge-active'}`}>
            <span className="admin-pricing-badge-dot" aria-hidden="true" />
            {archived ? 'Archived' : 'Active'}
          </span>
        </div>
      </div>
      <div className="admin-pricing-package-info">
        <span><strong>{currSym}{pkg.price}</strong> one-time</span>
        <span><strong>{pkg.credits}</strong> credits</span>
      </div>
      {pkg.description && <p className="admin-pricing-package-desc">{pkg.description}</p>}
      <div className="admin-pricing-package-meta">
        <span>Sort Order: <strong>{pkg.sort_order}</strong></span>
      </div>
      <div className="admin-pricing-package-actions">
        {!archived && (
          <button className="admin-pricing-action-btn" onClick={() => { setPackageError(''); setEditingPackageId(pkg.package_id); }}>Edit</button>
        )}
        {archived
          ? <button className="admin-pricing-action-btn admin-pricing-action-btn-success" onClick={() => handleToggleArchivePackage(pkg.package_id)}>Unarchive</button>
          : <button className="admin-pricing-action-btn admin-pricing-action-btn-warn" onClick={() => handleToggleArchivePackage(pkg.package_id)}>Archive</button>
        }
        <button className="admin-pricing-action-btn admin-pricing-action-btn-danger" onClick={() => setDeletingPackageId(pkg.package_id)}>Delete</button>
      </div>
    </div>
    );
  };

  // ── Render ───────────────────────────────────────────────────────────────────
  const content = (
    <div className="admin-pricing-container">
      {!embedded && <AdminTabNav />}

      {error && (
        <div className="admin-pricing-alert admin-pricing-alert-error">
          <svg fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" /></svg>
          {error}
          <button className="admin-pricing-alert-close" onClick={() => setError('')}>×</button>
        </div>
      )}
      {successMessage && (
        <div className="admin-pricing-alert admin-pricing-alert-success">
          <svg fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" /></svg>
          {successMessage}
        </div>
      )}

      <div className="admin-pricing-content">
        {/* ── General Settings ── */}
        <div className="admin-pricing-section">
          <div className="admin-pricing-section-header">
            <h2 className="admin-pricing-section-title">General Settings</h2>
            {hasGeneralChanges && (
              <button className="admin-pricing-btn-primary admin-pricing-btn-sm" onClick={handleSaveGeneral} disabled={savingGeneral}>
                {savingGeneral ? 'Saving…' : 'Save Changes'}
              </button>
            )}
          </div>
          {loadingGeneral ? <div className="admin-pricing-loading-spinner" /> : (
            <div className="admin-pricing-grid admin-pricing-grid-compact">
              <div className="admin-pricing-field">
                <label className="admin-pricing-label-sm">Trial Credits</label>
                <input type="number" className="admin-pricing-input-sm" value={trialCredits} min={0} onChange={e => setTrialCredits(parseInt(e.target.value) || 0)} />
                <p className="admin-pricing-help">Credits given to new users on signup</p>
              </div>
              <div className="admin-pricing-field">
                <label className="admin-pricing-label-sm">Seconds per Credit (Test Calls)</label>
                <input type="number" className="admin-pricing-input-sm" value={secondsPerCreditTest} min={1} onChange={e => setSecondsPerCreditTest(parseInt(e.target.value) || 1)} />
                <p className="admin-pricing-help">Call duration per credit for test calls</p>
              </div>
              <div className="admin-pricing-field">
                <label className="admin-pricing-label-sm">Seconds per Credit (Normal Calls)</label>
                <input type="number" className="admin-pricing-input-sm" value={secondsPerCreditNormal} min={1} onChange={e => setSecondsPerCreditNormal(parseInt(e.target.value) || 1)} />
                <p className="admin-pricing-help">Call duration per credit for campaign calls</p>
              </div>
              <div className="admin-pricing-field">
                <label className="admin-pricing-label-sm">Credits per SMS</label>
                <input type="number" className="admin-pricing-input-sm" value={creditsPerSms} min={0.01} step={0.01} onChange={e => setCreditsPerSms(parseFloat(e.target.value) || 0.1)} />
                <p className="admin-pricing-help">Credits deducted for each SMS sent</p>
              </div>
              <div className="admin-pricing-field">
                <label className="admin-pricing-label-sm">Credits per Email</label>
                <input type="number" className="admin-pricing-input-sm" value={creditsPerEmail} min={0.01} step={0.01} onChange={e => setCreditsPerEmail(parseFloat(e.target.value) || 0.1)} />
                <p className="admin-pricing-help">Credits deducted for each outbound email (balance rounds up like SMS)</p>
              </div>
              <div className="admin-pricing-field">
                <label className="admin-pricing-label-sm">Currency</label>
                <select className="admin-pricing-input-sm" value={currency} onChange={e => setCurrency(e.target.value)}>
                  <option value="usd">USD - US Dollar ($)</option>
                  <option value="eur">EUR - Euro (€)</option>
                  <option value="gbp">GBP - British Pound (£)</option>
                  <option value="aud">AUD - Australian Dollar (A$)</option>
                  <option value="cad">CAD - Canadian Dollar (C$)</option>
                  <option value="jpy">JPY - Japanese Yen (¥)</option>
                  <option value="cny">CNY - Chinese Yuan (¥)</option>
                  <option value="nzd">NZD - New Zealand Dollar (NZ$)</option>
                  <option value="sgd">SGD - Singapore Dollar (S$)</option>
                  <option value="inr">INR - Indian Rupee (₹)</option>
                </select>
                <p className="admin-pricing-help">Currency for all pricing (used in Stripe or Razorpay checkout, depending on region)</p>
              </div>
            </div>
          )}
        </div>

        {/* ── Subscription Plans ── */}
        <div className="admin-pricing-section">
          <div className="admin-pricing-section-header">
            <h2 className="admin-pricing-section-title">Subscription Plans</h2>
            {!addingPlan && (
              <button className="admin-pricing-btn-primary admin-pricing-btn-sm" onClick={() => { setPlanError(''); setAddingPlan(true); setEditingPlanId(null); }}>
                + Add New Plan
              </button>
            )}
          </div>

          {/* Inline add form */}
          {addingPlan && (
            <PlanInlineEdit initial={blankPlan()} isNew saving={planSaving} error={planError}
              onSave={handleAddPlan} onCancel={() => setAddingPlan(false)} />
          )}

          {loadingPlans ? <div className="admin-pricing-loading-spinner" /> : (
            <>
              {activePlans.length === 0 && !addingPlan && <p className="admin-pricing-empty">No active plans. Add one above.</p>}
              <div className="admin-pricing-packages">
                {activePlans.map(plan =>
                  editingPlanId === plan.plan_id
                    ? <PlanInlineEdit key={plan.plan_id} initial={plan} isNew={false} saving={planSaving} error={planError}
                        onSave={handleEditPlan} onCancel={() => setEditingPlanId(null)} />
                    : renderPlanCard(plan)
                )}
              </div>

              {archivedPlans.length > 0 && (
                <div className="admin-pricing-archived-section">
                  <button className="admin-pricing-archived-toggle" onClick={() => setShowArchivedPlans(p => !p)}>
                    {showArchivedPlans ? '▾' : '▸'} Archived Plans ({archivedPlans.length})
                  </button>
                  {showArchivedPlans && (
                    <div className="admin-pricing-packages admin-pricing-packages-archived">
                      {archivedPlans.map(plan => renderPlanCard(plan))}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* ── Credit Packages ── */}
        <div className="admin-pricing-section">
          <div className="admin-pricing-section-header">
            <h2 className="admin-pricing-section-title">Credit Packages</h2>
            {!addingPackage && (
              <button className="admin-pricing-btn-primary admin-pricing-btn-sm" onClick={() => { setPackageError(''); setAddingPackage(true); setEditingPackageId(null); }}>
                + Add New Package
              </button>
            )}
          </div>

          {/* Inline add form */}
          {addingPackage && (
            <PackageInlineEdit initial={blankPackage()} isNew saving={packageSaving} error={packageError}
              onSave={handleAddPackage} onCancel={() => setAddingPackage(false)} />
          )}

          {loadingPackages ? <div className="admin-pricing-loading-spinner" /> : (
            <>
              {activePackages.length === 0 && !addingPackage && <p className="admin-pricing-empty">No active packages. Add one above.</p>}
              <div className="admin-pricing-packages">
                {activePackages.map(pkg =>
                  editingPackageId === pkg.package_id
                    ? <PackageInlineEdit key={pkg.package_id} initial={pkg} isNew={false} saving={packageSaving} error={packageError}
                        onSave={handleEditPackage} onCancel={() => setEditingPackageId(null)} />
                    : renderPackageCard(pkg)
                )}
              </div>

              {archivedPackages.length > 0 && (
                <div className="admin-pricing-archived-section">
                  <button className="admin-pricing-archived-toggle" onClick={() => setShowArchivedPackages(p => !p)}>
                    {showArchivedPackages ? '▾' : '▸'} Archived Packages ({archivedPackages.length})
                  </button>
                  {showArchivedPackages && (
                    <div className="admin-pricing-packages admin-pricing-packages-archived">
                      {archivedPackages.map(pkg => renderPackageCard(pkg))}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Only modals: confirm delete + unsaved nav guard */}
      {deletingPlanId && <ConfirmDeleteModal label={deletingPlanLabel} activeUsers={deletingPlanActiveUsers} onConfirm={() => handleDeletePlan(false)} onForceDelete={() => handleDeletePlan(true)} onCancel={() => { setDeletingPlanId(null); setDeletingPlanActiveUsers(0); }} />}
      {deletingPackageId && <ConfirmDeleteModal label={deletingPackageLabel} onConfirm={handleDeletePackage} onCancel={() => setDeletingPackageId(null)} />}
      {showLeaveModal && (
        <div className="admin-pricing-unsaved-overlay" role="dialog" aria-modal="true">
          <div className="admin-pricing-unsaved-modal">
            <h2 className="admin-pricing-unsaved-title">Unsaved changes</h2>
            <p className="admin-pricing-unsaved-text">You have unsaved changes to General Settings. Leave without saving?</p>
            <div className="admin-pricing-unsaved-actions">
              <button className="admin-pricing-unsaved-btn admin-pricing-unsaved-btn-secondary" onClick={() => setShowLeaveModal(false)}>Stay</button>
              <button className="admin-pricing-unsaved-btn admin-pricing-unsaved-btn-danger" onClick={() => { setShowLeaveModal(false); navigate(pendingPath!); }}>Leave without saving</button>
              <button className="admin-pricing-unsaved-btn admin-pricing-unsaved-btn-primary" onClick={async () => { await handleSaveGeneral(); setShowLeaveModal(false); navigate(pendingPath!); }} disabled={savingGeneral}>{savingGeneral ? 'Saving…' : 'Save and go'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  if (embedded) return content;
  return (
    <div className="admin-pricing-page">
      <div className="admin-pricing-bg">
        <div className="admin-pricing-bg-1" />
        <div className="admin-pricing-bg-2" />
      </div>
      {content}
    </div>
  );
};

export default AdminPricingSettingsPage;
